import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TabNavigation from '../../components/ui/TabNavigation';
import AudioPlayerBar from '../../components/ui/AudioPlayerBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BroadcastControls from './components/BroadcastControls';
import AudioLevelMeter from './components/AudioLevelMeter';
import WaveformVisualizer from './components/WaveformVisualizer';
import BroadcastMetadata from './components/BroadcastMetadata';
import LiveChatPanel from './components/LiveChatPanel';
import AudioSettings from './components/AudioSettings';
import StreamHealthMonitor from './components/StreamHealthMonitor';
import ConfirmationModal from './components/ConfirmationModal';
import { MOCK_CHAT_MESSAGES, MOCK_CURRENT_AUDIO, DEFAULT_METADATA, DEFAULT_AUDIO_SETTINGS } from '../../constants/studioData';

const LiveBroadcastingStudio = () => {
  const navigate = useNavigate();
  const [isLive, setIsLive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [listenerCount, setListenerCount] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [peakLevel, setPeakLevel] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('excellent');
  const [showStopModal, setShowStopModal] = useState(false);
  const [validationError, setValidationError] = useState('');

  const [metadata, setMetadata] = useState(DEFAULT_METADATA);
  const [audioSettings, setAudioSettings] = useState(DEFAULT_AUDIO_SETTINGS);
  const [chatMessages, setChatMessages] = useState(MOCK_CHAT_MESSAGES);
  const [currentAudio] = useState(MOCK_CURRENT_AUDIO);

  useEffect(() => {
    let interval;
    if (isLive && !isPaused) {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1);
        setAudioLevel(Math.random() * 100);
        setPeakLevel(Math.random() * 100);

        if (Math.random() > 0.7) {
          setListenerCount((prev) => Math.max(0, prev + Math.floor(Math.random() * 3) - 1));
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isLive, isPaused]);

  const handleStartBroadcast = useCallback(() => {
    if (!metadata?.title?.trim()) {
      setValidationError('Пожалуйста, укажите название эфира');
      return;
    }
    setValidationError('');
    setIsLive(true);
    setListenerCount(Math.floor(Math.random() * 50) + 10);
  }, [metadata?.title]);

  const handleStopBroadcast = useCallback(() => {
    setShowStopModal(true);
  }, []);

  const confirmStopBroadcast = useCallback(() => {
    setIsLive(false);
    setIsPaused(false);
    setDuration(0);
    setListenerCount(0);
    setAudioLevel(0);
    setPeakLevel(0);
    setShowStopModal(false);
  }, []);

  const handlePauseResume = useCallback(() => {
    setIsPaused(!isPaused);
  }, [isPaused]);

  return (
    <>
      <Helmet>
        <title>{isLive ? '🔴 LIVE: ' : ''}Студия вещания | radio.co.im</title>
        <meta name="description" content="Создайте свой аутентичный голосовой эфир на radio.co.im - платформе для философских размышлений и присутствия" />
        <meta property="og:title" content="Студия вещания | radio.co.im" />
        <meta property="og:description" content="Создайте свой аутентичный голосовой эфир" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <div className="live-broadcasting-studio min-h-screen bg-background">
        <TabNavigation />
        <main 
          className="studio-content max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-20 lg:pb-8"
          role="main"
          aria-label="Студия вещания">
          
          <div className="studio-header mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
                  Студия вещания
                </h1>
                <p className="text-sm lg:text-base text-muted-foreground">
                  Создайте свой аутентичный голосовой эфир
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="default"
                  iconName="Settings"
                  iconPosition="left"
                  onClick={() => setShowSettings(!showSettings)}
                  aria-label="Настройки аудио"
                  aria-pressed={showSettings}>
                  <span className="hidden sm:inline">Настройки</span>
                </Button>

                <Button
                  variant="ghost"
                  size="default"
                  iconName="X"
                  onClick={() => navigate('/main-dashboard')}
                  aria-label="Закрыть студию вещания">
                  <span className="hidden sm:inline">Закрыть</span>
                </Button>
              </div>
            </div>

            {validationError && (
              <div 
                className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg"
                role="alert"
                aria-live="polite">
                <div className="flex items-center gap-2 text-error">
                  <Icon name="AlertCircle" size={20} />
                  <span className="text-sm font-medium">{validationError}</span>
                </div>
              </div>
            )}
          </div>

          <div className="studio-layout grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="studio-main lg:col-span-8 space-y-6">
              <BroadcastControls
                isLive={isLive}
                isPaused={isPaused}
                onStartBroadcast={handleStartBroadcast}
                onStopBroadcast={handleStopBroadcast}
                onPauseResume={handlePauseResume}
                listenerCount={listenerCount}
                duration={duration} />


              <div className="audio-monitoring grid grid-cols-1 md:grid-cols-2 gap-6">
                <AudioLevelMeter
                  level={audioLevel}
                  peakLevel={peakLevel}
                  isActive={isLive && !isPaused}
                  label="Входной уровень" />


                <StreamHealthMonitor
                  isLive={isLive}
                  connectionQuality={connectionQuality}
                  bitrate={parseInt(audioSettings?.bitrate)}
                  latency={Math.floor(Math.random() * 100) + 50}
                  droppedFrames={Math.floor(Math.random() * 5)} />

              </div>

              <WaveformVisualizer
                isActive={isLive && !isPaused}
                audioData={Array.from({ length: 64 }, () => Math.random())}
                height={200} />


              {showSettings ?
              <AudioSettings
                settings={audioSettings}
                onSettingsChange={setAudioSettings}
                disabled={isLive} /> :


              <BroadcastMetadata
                metadata={metadata}
                onMetadataChange={setMetadata}
                disabled={isLive} />

              }
            </div>

            <div className="studio-sidebar lg:col-span-4">
              <div className="sticky top-24">
                <LiveChatPanel
                  isLive={isLive}
                  messages={isLive ? chatMessages : []} />

              </div>
            </div>
          </div>

          {!isLive && (
            <div className="studio-tips mt-8 bg-card rounded-lg border border-border p-6 shadow-soft">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <Icon name="Lightbulb" size={20} color="var(--color-primary)" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    Советы для успешного эфира
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground" role="list">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>Используйте качественный микрофон и наушники для предотвращения эха</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>Проверьте стабильность интернет-соединения перед началом трансляции</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>Заполните метаданные эфира для лучшей видимости в поиске</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>Взаимодействуйте со слушателями через чат для создания вовлечённости</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </main>
        
        <AudioPlayerBar
          audioSource={currentAudio}
          initialPlaybackState="paused"
          onPlayPause={() => {}}
          onSeek={() => {}}
          onVolumeChange={() => {}}
          onNext={() => {}}
          onPrevious={() => {}} />

        <ConfirmationModal
          isOpen={showStopModal}
          onClose={() => setShowStopModal(false)}
          onConfirm={confirmStopBroadcast}
          title="Завершить трансляцию?"
          message="Вы уверены, что хотите завершить трансляцию? Эфир будет остановлен для всех слушателей."
          confirmText="Завершить"
          cancelText="Отмена" />
      </div>
    </>
  );
};

export default LiveBroadcastingStudio;